import React, { useEffect, useState } from 'react';
import { Typography, Switch, Spin, Alert, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { getFeatureFlag, updateFeatureFlag } from '../../../services/AdminService';
import { useSiteMode } from '../../../contextProviders/SiteModeProvider';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title, Text } = Typography;

interface IFlagRow {
  /** PlatformFeatureFlag.Key -- must match the backend's FeatureFlagKeys constant. */
  key: string;
  labelKey: string;
  descriptionKey: string;
  /**
   * True for the flags SiteModeProvider caches, so flipping one re-reads it
   * and the admin's own session stops disagreeing with what it just did.
   */
  affectsSiteMode?: boolean;
}

// Every flag this panel exposes. AdminFeatureFlagsController is a generic
// key/value store, so a new one is a row here and nothing else -- no new
// endpoint, no new service call.
const FLAG_ROWS: IFlagRow[] = [
  {
    key: 'ServicesEnabled',
    labelKey: TRANSLATIONS.ADMIN_FEATURE_FLAG_SERVICES_ENABLED_LABEL,
    descriptionKey: TRANSLATIONS.ADMIN_FEATURE_FLAG_SERVICES_ENABLED_DESCRIPTION,
  },
  {
    key: 'LandingModeEnabled',
    labelKey: TRANSLATIONS.ADMIN_FEATURE_FLAG_LANDING_MODE_LABEL,
    descriptionKey: TRANSLATIONS.ADMIN_FEATURE_FLAG_LANDING_MODE_DESCRIPTION,
    affectsSiteMode: true,
  },
  {
    key: 'RegistrationEnabled',
    labelKey: TRANSLATIONS.ADMIN_FEATURE_FLAG_REGISTRATION_ENABLED_LABEL,
    descriptionKey: TRANSLATIONS.ADMIN_FEATURE_FLAG_REGISTRATION_ENABLED_DESCRIPTION,
    affectsSiteMode: true,
  },
];

/**
 * Admin-only (see Admin.tsx's own ADMIN_ONLY_SECTIONS, matching this
 * controller's [Authorize(Roles = AdminRoles.Admin)] -- a Moderator never
 * sees this section) toggle over PlatformFeatureFlag rows. A flag nobody
 * has ever set reads as "off, never configured" (see getFeatureFlag's own
 * doc comment) -- flipping it on for the first time is what actually
 * creates the row (AdminFeatureFlagsController.UpdateFlag upserts).
 *
 * Which is exactly why RegistrationEnabled is seeded as an explicit `false`
 * row rather than left absent: "missing" and "off" mean the same thing to
 * IFeatureFlagService, and the difference between a door nobody has opened
 * yet and a door somebody closed on purpose should be visible here.
 */
const FeatureFlagsSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const { refresh: refreshSiteMode } = useSiteMode();
  const [enabledByKey, setEnabledByKey] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  useEffect(() => {
    Promise.all(FLAG_ROWS.map((row) => getFeatureFlag(row.key).then((flag) => [row.key, flag?.enabled ?? false] as const)))
      .then((entries) => setEnabledByKey(Object.fromEntries(entries)))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (row: IFlagRow, checked: boolean) => {
    setSavingKey(row.key);
    try {
      const flag = await updateFeatureFlag(row.key, checked);
      setEnabledByKey((prev) => ({ ...prev, [row.key]: flag.enabled }));
      if (row.affectsSiteMode) await refreshSiteMode();
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_FEATURE_FLAGS)}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_FEATURE_FLAGS_DESCRIPTION)}
      </Text>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_FEATURE_FLAGS_LOAD_ERROR)} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {FLAG_ROWS.map((row) => (
            <div
              key={row.key}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: 16,
                background: token.colorFillQuaternary,
                border: `1px solid ${token.colorBorderSecondary}`,
                borderRadius: 8,
              }}
            >
              <Switch
                checked={enabledByKey[row.key] ?? false}
                loading={savingKey === row.key}
                onChange={(checked) => handleToggle(row, checked)}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 500 }}>{translate(t, row.labelKey)}</div>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {translate(t, row.descriptionKey)}
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FeatureFlagsSection;
